import React, { useEffect, useRef, useState } from 'react'
import { IProduct } from '../../../../types/products'
import './AdminMenuItem.scss'
import { imageUrl } from '../../../../utils/imageUrl'
import ProductsModal from '../../pages/products-page/products-modal/ProductsModal'
import { useDeleteProductMutation } from '../../../../store/api/productsApi'
import { Store } from '../../../../utils/toast'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import ConfirmDialog from '../../../../components/confirm-dialog/ConfirmDialog'

import { ICategory } from '../../../../types/categories'
import { IProductGroup } from '../../../../types/app'
import { useGetIngredientsQuery } from '../../../../store/api/ingredientsApi'
import { IIngredient } from '../../../../types/ingredients'
import { getAppType } from '../../../../utils/appType'

type Props = {
  categories?: ICategory[]
  data?: IProduct
  setData?: React.Dispatch<React.SetStateAction<IProductGroup[]>>
}
const c = 'admin-menu-item'

const AdminMenuItem = (props: Props) => {
  const [openEdit, setOpenEdit] = useState(false)
  const [confirmEdit, setConfirmEdit] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteProduct] = useDeleteProductMutation()
  const { t } = useTranslation()
  const savedScrollRef = useRef(0)
  const { data: allIngredients = [] } = useGetIngredientsQuery()
  const linkedIngredients = allIngredients.filter((ing: IIngredient) =>
    props.data?.availableIngredients?.includes(ing._id),
  )

  useEffect(() => {
    if (openEdit) {
      savedScrollRef.current = window.scrollY
    } else {
      const saved = savedScrollRef.current
      if (saved) {
        setTimeout(() => window.scrollTo({ top: saved }), 50)
      }
    }
  }, [openEdit])

  function handleDeleteProduct() {
    props.setData?.((prev: IProductGroup[]) => {
      return prev.map((item: IProductGroup) => {
        if (item.items) {
          return {
            ...item,
            items: item.items.filter(
              (product: IProduct) => product._id !== props.data?._id,
            ),
          }
        }
        return item
      })
    })
    deleteProduct(String(props.data?._id))
      .unwrap()
      .then(() => {
        Store.addNotification({
          title: 'SUCCESS',
          message: 'Produsul a fost sters cu succes!',
          container: 'top-center',
          type: 'success',
          insert: 'top',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: { duration: 1000, onScreen: true },
        })
      })
  }

  return (
    <div className={c}>
      <div className={`${c}__image`}>
        <div className={`${c}__image-badges`}>
          {props.data?.isNew && (
            <span className={`${c}__image-badges-badge new`}>Nou</span>
          )}
          {props.data?.isAdditional && (
            <span className={`${c}__image-badges-badge additional`}>
              Adițional
            </span>
          )}
          {props.data?.discount && (
            <span className={`${c}__image-badges-badge discount`}>
              -{props.data?.discount}%
            </span>
          )}
        </div>
        <img
          src={imageUrl(props.data?.image, 480)}
          alt='item'
          loading='lazy'
          decoding='async'
          onError={({ currentTarget }) => {
            currentTarget.onerror = null
            currentTarget.src =
              getAppType() === 'catering'
                ? '/panda-catering-logo-bamboo.png'
                : '/logo_bg.png'
          }}
        />
      </div>

      <div className={`${c}__info`}>
        <div className={`${c}__info-name`}>{props.data?.name}</div>
        <div className={`${c}__info-meta`}>
          {props.data?.weight ? (
            <span className={`${c}__info-meta-tag`}>{props.data.weight}g</span>
          ) : null}
          {props.data?.pieces ? (
            <span className={`${c}__info-meta-tag`}>
              {props.data.pieces} {t('PIECES')}
            </span>
          ) : null}
        </div>
        <div className={`${c}__info-price`}>{props.data?.price} lei</div>
        {linkedIngredients.length > 0 && (
          <div className={`${c}__info-ingredients`}>
            {linkedIngredients.map((ing: IIngredient) => (
              <span key={ing._id} className={`${c}__info-ingredients-tag`}>
                {ing.label}
                {(ing.customer_price ?? 0) > 0
                  ? ` +${ing.customer_price} lei`
                  : ''}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className={`${c}__actions`}>
        <button
          className={`${c}__actions-btn edit`}
          onClick={() => setConfirmEdit(true)}
          title='Editează'
        >
          <FontAwesomeIcon icon={faEdit} />
        </button>
        <button
          className={`${c}__actions-btn delete`}
          onClick={() => setConfirmDelete(true)}
          title='Șterge'
        >
          <FontAwesomeIcon icon={faTrashCan} />
        </button>
      </div>

      <ConfirmDialog
        open={confirmEdit}
        title='Editează produs'
        message={`Ești sigur că vrei să editezi "${props.data?.name}"?`}
        onConfirm={() => {
          setConfirmEdit(false)
          setOpenEdit(true)
        }}
        onCancel={() => setConfirmEdit(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title='Șterge produs'
        message={`Ești sigur că vrei să ștergi "${props.data?.name}"? Această acțiune este ireversibilă.`}
        onConfirm={() => {
          setConfirmDelete(false)
          handleDeleteProduct()
        }}
        onCancel={() => setConfirmDelete(false)}
      />

      <ProductsModal
        isOpen={openEdit}
        setIsOpen={setOpenEdit}
        categories={props.categories}
        data={props.data}
        edit
      />
    </div>
  )
}

export default AdminMenuItem
